import React, { useCallback, useMemo, Fragment } from 'react';
import cn from 'classnames';
import { Spinner } from '@creditflowresearch/uilib/Loader/Spinner';
import {
  BodyWrapper,
  HeaderRow,
  HeaderWrapper,
  FooterWrapper,
  FooterRow,
} from '@creditflowresearch/uilib/Table/DataTable';
import { DEFAULT_EMPTY_MESSAGE } from '@creditflowresearch/uilib/Table/DataTable/constants';
import dataTableStyles from '@creditflowresearch/uilib/Table/DataTable/dataTable.module.scss';
import { IColumn, ISortChange } from '@creditflowresearch/uilib/Table/DataTable/interfaces';
import ExpandableRow from './ExpandableRow';

export interface IProps {
  /**
   * Optional CSS class name for custom styling
   */
  className?: string,
  /**
   * Array of column definitions for the table
   */
  columns: IColumn[],
  /**
   * Collection of data objects to be displayed in the table
   */
  collection: {
    [key: string]: any,
  }[],
  /**
   * Boolean indicating if data is being fetched
   */
  isFetching: boolean,
  /**
   * Optional key to sort by
   */
  sortBy?: string,
  /**
   * Optional sort order ('asc' or 'desc')
   */
  sortOrder?: string,
  /**
   * Callback function to handle sorting changes
   */
  onSortChange?: ISortChange,
  /**
   * Component to render the content of expandable rows
   */
  expandableRowContent: React.ElementType,
  /**
   * Message to display when the collection is empty
   */
  emptyCollectionMessage?: string,
  /**
   * Unique identifier for testing purposes
   */
  dataTest: string,
  /**
   * Indicating if rows are expanded
   * needed to expand/collapse rows from outside
   */
  isRowsExpanded?: boolean,
  /**
   * Indicating if rows should be expanded/collapsed on row click
   */
  expandCollapseOnRowClick?: boolean,
  /**
   * Array of row indexes that should not be expandable
   */
  nonExpandableRowIndexes?: number[],
  /**
   * Array of row indexes that should be expanded by default
   */
  defaultExpandedRowIndexes?: number[],
  /**
   * Custom row click handler
   * Will be called in addition to the expand/collapse functionality
   * @param e The click event
   * @param params Object containing row data, index, and expanded state
   */
  onRowClick?: (
    e: React.MouseEvent<HTMLTableRowElement>,
    params: { row: { [key: string]: any }, index: number, isExpanded: boolean },
  ) => void,
  /**
   * Custom row wrapper
   */
  RowWrapper?: React.ElementType,
  /**
   * Whether to force the row wrapper to be used
   */
  forceRowWrapper?: boolean,
  /**
   * Flag to show or hide the table footer.
   */
  isFooterVisible?: boolean,
  /**
   * Custom Footer component
   */
  Footer?: React.ElementType,
  /**
   * Callback to compute a CSS class name for each row based on its data.
   * Use for custom row styling (disabled, selected, etc.).
   */
  rowClassNameCallback?: (rowData: Record<string, any>) => string,
  /**
   * Set of IDs to highlight with a yellow flash (live update indicator).
   * Used together with `highlightKey` to determine which row field to match.
   */
  highlightedIds?: Set<string | number>,
  /**
   * The row data field to match against `highlightedIds`. Defaults to 'id'.
   */
  highlightKey?: string,
}

const ExpandableTable = ({
  className,
  columns,
  collection,
  isFetching,
  sortBy,
  sortOrder,
  onSortChange,
  dataTest,
  expandableRowContent,
  emptyCollectionMessage = DEFAULT_EMPTY_MESSAGE,
  isRowsExpanded = false,
  expandCollapseOnRowClick = true,
  nonExpandableRowIndexes = [],
  defaultExpandedRowIndexes = [],
  onRowClick,
  forceRowWrapper = false,
  isFooterVisible = false,
  RowWrapper = Fragment,
  Footer,
  rowClassNameCallback,
  highlightedIds,
  highlightKey = 'id',
}: IProps) => {
  const columnsLength = useMemo(() => (
    columns.filter((column) => column.isVisible || column.isVisible === undefined).length
  ), [columns]);

  const combinedRowClassNameCallback = useMemo(() => {
    if (!highlightedIds && !rowClassNameCallback) return undefined;

    return (rowData: Record<string, any>): string => cn(
      highlightedIds?.has(rowData[highlightKey]) && dataTableStyles.highlightedRow,
      rowClassNameCallback?.(rowData),
    );
  }, [highlightedIds, highlightKey, rowClassNameCallback]);

  const renderRow = useCallback((row: { [key: string]: any }, index: number) => (
    <ExpandableRow
      key={ index }
      index={ index }
      row={ row }
      columns={ columns }
      columnsLength={ columnsLength }
      expandableRowContent={ expandableRowContent }
      isExpandedExternal={ isRowsExpanded }
      expandCollapseOnRowClick={ expandCollapseOnRowClick }
      isNonExpandable={ nonExpandableRowIndexes.includes(index) }
      isDefaultExpanded={ defaultExpandedRowIndexes.includes(index) }
      onRowClick={ onRowClick }
      RowWrapper={ RowWrapper }
      forceRowWrapper={ forceRowWrapper }
      rowClassNameCallback={ combinedRowClassNameCallback }
    />
  ), [
    columns,
    columnsLength,
    expandableRowContent,
    isRowsExpanded,
    expandCollapseOnRowClick,
    nonExpandableRowIndexes,
    defaultExpandedRowIndexes,
    onRowClick,
    combinedRowClassNameCallback,
  ]);

  const footer = Footer ? <Footer /> : (
    <FooterWrapper>
      <FooterRow columns={ columns } />
    </FooterWrapper>
  );

  return (
    <div
      className={ cn(dataTableStyles.dataTableContainer, className) }
      data-test={ `${ dataTest }Container` }
    >
      <Spinner overlay isVisible={ isFetching } />
      <table className={ cn(dataTableStyles.dataTable, dataTableStyles.isHoverable) }>
        <HeaderWrapper>
          <HeaderRow
            sortBy={ sortBy }
            sortOrder={ sortOrder }
            onSortChange={ onSortChange }
            columns={ columns }
          />
        </HeaderWrapper>
        <BodyWrapper>
          { collection.length > 0
            ? collection.map(renderRow)
            : (
              <tr>
                <td
                  colSpan={ columnsLength }
                  className={ dataTableStyles.emptyMessage }
                >
                  <div
                    className={ dataTableStyles.emptyMessage }
                    data-test={ `${ dataTest }EmptyMessage` }
                  >
                    { emptyCollectionMessage }
                  </div>
                </td>
              </tr>
            ) }
        </BodyWrapper>
        { isFooterVisible && footer }
      </table>
    </div>
  );
};

export default ExpandableTable;
